import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { useChats } from '../hooks/useChat';
import PageSpinner from './PageSpinner';
import PersonIcon from './PersonIcon';
import { cachedSupportAdminUid } from '../firebase/supportService';
import { SUPPORT_DISPLAY_NAME, SUPPORT_AVATAR } from '../constants/support';

function ago(ts, locale) {
  const d = ts?.toDate?.();
  if (!d) return '';
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return '';
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h`;
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}

/** The other participant, read off the chat document — no profile lookups. */
export function otherParticipant(chat, myUid) {
  if (!chat || !myUid) return null;
  const uid = (chat.participants || []).find(p => p !== myUid);

  // ── THE SUPPORT ACCOUNT WEARS THE BRAND, NOT ITS OWNER ───────────────────
  // Whoever is on duty is a real person with a real profile, and a
  // conversation with support opened as "Rick Sanches" with his photograph.
  // A user writing to support is writing to the company.
  //
  // OVERRIDDEN HERE RATHER THAN AT CREATION, because participantInfo may
  // already hold the admin's real details from a conversation that predates
  // this — and because support can change hands, which would otherwise make
  // every existing thread appear to be from someone new.
  //
  // One place covers every surface: the conversation list, the thread header
  // and the share picker all resolve the other party through this function.
  if (uid && uid === cachedSupportAdminUid()) {
    return { uid, name: SUPPORT_DISPLAY_NAME, avatar: SUPPORT_AVATAR };
  }

  const info = chat.participantInfo?.[uid] || {};
  return { uid, name: info.name || '', avatar: info.avatar || '' };
}

// ── CHAT LIST ────────────────────────────────────────────────────────────────
// My conversations. Extracted so the overlay and the standalone page render the
// SAME list from the same hook — two copies would drift, and each would open
// its own listener.
export default function ChatList({ selectedId, onSelect }) {
  const { user } = useAuth();
  const { t, lang } = useT();
  const { chats, loading, hide } = useChats();
  // Which row is asking to be removed. Hiding is reversible, but it still
  // takes a conversation off your list, so it asks once.
  const [confirmId, setConfirmId] = useState(null);

  // A spinner while fetching, the dashed box only for a genuinely empty list —
  // using `.sp-empty` for both made "still loading" and "no conversations"
  // look identical.
  if (loading) return <PageSpinner label={t('chat_loading')} />;
  if (!chats.length) return <p className="sp-empty">{t('chat_none')}</p>;

  const target = chats.find(c => c.id === confirmId);

  return (
    <>
    <ul className="msg-rows">
      {chats.map(c => {
        const o = otherParticipant(c, user?.uid);
        const unread = c.unread?.[user?.uid] || 0;
        const mine = c.lastSenderUid === user?.uid;
        return (
          <li key={c.id} className="msg-li">
            <button
              type="button"
              className={`msg-row${c.id === selectedId ? ' msg-row--on' : ''}`}
              onClick={() => onSelect(c.id)}
            >
              <span className="msg-avatar">
                {o?.avatar ? <img src={o.avatar} alt="" /> : <PersonIcon />}
              </span>
              <span className="msg-row-body">
                <span className="msg-row-top">
                  <span className="msg-row-name">{o?.name || t('chat_someone')}</span>
                  <span className="msg-row-ago">{ago(c.lastMessageAt, lang)}</span>
                </span>
                <span className="msg-row-last">
                  {mine && c.lastMessage ? `${t('chat_you')} ` : ''}
                  {c.lastMessage || t('chat_no_messages')}
                </span>
              </span>
              {unread > 0 && <span className="msg-unread">{unread > 9 ? '9+' : unread}</span>}
            </button>

              {/* Removes the thread from MY list only — the other side keeps
                  theirs. See hideChat for why this is not a delete. */}
              <button
                type="button"
                className="msg-del"
                title={t('chat_delete')}
                aria-label={t('chat_delete')}
                onClick={(e) => { e.stopPropagation(); setConfirmId(c.id); }}
              >✕</button>
          </li>
        );
      })}
    </ul>

    {/* Centred over the whole panel rather than inside the row: the question is
        two lines long and the row is 60px tall, so in-place it overlapped the
        conversation it was asking about. */}
    {target && (
      <div className="msg-confirm" role="dialog" aria-modal="true"
        onClick={() => setConfirmId(null)}>
        <div className="msg-confirm-box" onClick={e => e.stopPropagation()}>
          <p className="msg-confirm-who">
            {otherParticipant(target, user?.uid)?.name || t('chat_someone')}
          </p>
          <p className="msg-confirm-text">{t('chat_delete_confirm')}</p>
          <div className="msg-confirm-row">
            <button type="button" className="ct-edit-btn"
              onClick={() => setConfirmId(null)}>{t('cancel')}</button>
            <button type="button" className="ct-edit-btn ct-edit-btn--danger"
              onClick={() => {
                const id = target.id;
                setConfirmId(null);
                hide(id);
                if (selectedId === id) onSelect('');
              }}>
              {t('delete_tooltip')}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
